import { useState, useEffect } from "react";
import { Star, TrendingUp, AlertCircle, CheckCircle, Flag, Lightbulb, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Star Display Component (read-only)
const StarDisplay = ({ rating, size = "sm" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating
              ? "fill-amber-400 text-amber-400"
              : star - 0.5 <= rating
              ? "fill-amber-400/50 text-amber-400"
              : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
};

// Profile Completeness Card
const ProfileCompletenessCard = ({ completeness }) => {
  if (!completeness) return null;

  const { score, breakdown } = completeness;

  const categories = [
    { key: "business_info", label: "Business Info", max: 20 },
    { key: "skills_categories", label: "Skills & Categories", max: 15 },
    { key: "insurance_verified", label: "Insurance Verified", max: 20 },
    { key: "availability_set", label: "Availability", max: 10 },
    { key: "profile_photo_desc", label: "Photo & Description", max: 10 },
    { key: "ratings_performance", label: "Ratings & Performance", max: 25 },
  ];

  return (
    <Card className="border-gray-100">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#0FA3B1]" />
          Profile Completeness
        </CardTitle>
        <CardDescription>
          Complete your profile to rank higher in lead sorting
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Score */}
        <div className="flex items-center gap-4">
          <div className="text-3xl font-bold text-[#0FA3B1]">{score}%</div>
          <Progress value={score} className="flex-1 h-3" />
        </div>

        {/* Breakdown */}
        <div className="space-y-2">
          {categories.map((cat) => {
            const value = breakdown?.[cat.key] || 0;
            const percentage = (value / cat.max) * 100;
            return (
              <div key={cat.key} className="flex items-center gap-2 text-sm">
                <span className="w-40 text-[#1F2933]/70">{cat.label}</span>
                <Progress value={percentage} className="flex-1 h-2" />
                <span className="w-12 text-right text-[#1F2933]/50">
                  {value}/{cat.max}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Performance Tips Card
const PerformanceTipsCard = ({ tips }) => {
  if (!tips || tips.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-600" />
          Performance Tips
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {tips.map((tip) => (
            <li key={tip} className="flex items-start gap-2 text-sm text-amber-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {tip}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// Recent Ratings List
const RecentRatingsList = ({ ratings, onFlag }) => {
  if (!ratings || ratings.length === 0) {
    return (
      <div className="text-center py-8 text-[#1F2933]/50">
        <Star className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No reviews yet</p>
        <p className="text-sm">Complete jobs to receive reviews from homeowners</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {ratings.map((rating, idx) => (
        <div
          key={rating.rating_id || idx}
          className="bg-gray-50 rounded-xl p-4 border border-gray-100"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <StarDisplay rating={rating.overall_rating} size="md" />
              <span className="font-medium text-[#1F2933]">
                {rating.overall_rating}/5
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#1F2933]/50">
                {new Date(rating.created_at).toLocaleDateString()}
              </span>
              {rating.status === "active" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-[#1F2933]/50 hover:text-red-600"
                  onClick={() => onFlag(rating)}
                >
                  <Flag className="w-3 h-3 mr-1" /> Flag
                </Button>
              )}
              {rating.status === "flagged" && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  Under Review
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-2">
            <div className="flex items-center gap-1 text-[#1F2933]/70">
              <span className="text-xs">Professional:</span>
              <StarDisplay rating={rating.professional_rating || rating.professionalism_rating || rating.overall_rating} size="sm" />
            </div>
            <div className="flex items-center gap-1 text-[#1F2933]/70">
              <span className="text-xs">On Time:</span>
              <StarDisplay rating={rating.on_time_rating || rating.overall_rating} size="sm" />
            </div>
            <div className="flex items-center gap-1 text-[#1F2933]/70">
              <span className="text-xs">Quality:</span>
              <StarDisplay rating={rating.quality_rating || rating.overall_rating} size="sm" />
            </div>
            <div className="flex items-center gap-1 text-[#1F2933]/70">
              <span className="text-xs">Cleanup:</span>
              <StarDisplay rating={rating.cleanup_rating || rating.overall_rating} size="sm" />
            </div>
          </div>

          {rating.comment && (
            <p className="text-sm text-[#1F2933]/80 italic mt-2">
              &ldquo;{rating.comment}&rdquo;
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default function ContractorRatingsSection() {
  const [ratingsData, setRatingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/contractor/ratings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRatingsData(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const handleFlagClick = (rating) => {
    setSelectedRating(rating);
    setFlagReason("");
    setFlagDialogOpen(true);
  };

  const handleFlagSubmit = async () => {
    if (!flagReason.trim()) {
      toast.error("Please provide a reason for flagging");
      return;
    }

    setFlagging(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/ratings/${selectedRating.rating_id}/flag`,
        { reason: flagReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Rating flagged for review");
      setFlagDialogOpen(false);
      fetchRatings(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to flag rating");
    } finally {
      setFlagging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8 border-4 border-[#0FA3B1]/20 border-t-[#0FA3B1] rounded-full" />
      </div>
    );
  }

  const { summary, recent_ratings, profile_completeness, performance_tips, new_contractor_status } = ratingsData || {};

  return (
    <div className="space-y-6">
      {/* New on Contractor Hub Banner */}
      {new_contractor_status?.is_new && (
        <Card className="border-[#0FA3B1]/30 bg-gradient-to-r from-[#0FA3B1]/5 to-[#7ED957]/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0FA3B1]/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#0FA3B1]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-[#1F2933]">New Contractor</h3>
                  <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] text-xs">
                    {new_contractor_status.completed_jobs} / {new_contractor_status.job_threshold} jobs
                  </Badge>
                </div>
                <p className="text-sm text-[#1F2933]/60">
                  Ratings stabilize after the first few completed jobs to ensure fairness.
                  Complete {new_contractor_status.job_threshold - new_contractor_status.completed_jobs} more jobs 
                  or wait {Math.max(0, new_contractor_status.days_threshold - new_contractor_status.days_since_activation)} days to graduate.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Average Rating */}
        <Card className="border-gray-100">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-4xl font-bold text-[#1F2933]">
                  {summary?.display_rating?.toFixed(1) || summary?.average_rating?.toFixed(1) || "5.0"}
                </span>
                <Star className="w-8 h-8 fill-amber-400 text-amber-400" />
              </div>
              <StarDisplay rating={summary?.display_rating || summary?.average_rating || 5} size="lg" />
              <p className="text-sm text-[#1F2933]/50 mt-2">
                {summary?.rating_count || 0} {summary?.rating_count === 1 ? "review" : "reviews"}
                {new_contractor_status?.is_new && summary?.rating_count === 0 && (
                  <span className="block text-xs text-[#0FA3B1]">New Contractor</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Professional */}
        <Card className="border-gray-100">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#1F2933]/50 mb-1">Professional</p>
              <div className="text-2xl font-bold text-[#1F2933]">
                {summary?.avg_professional?.toFixed(1) || summary?.avg_professionalism?.toFixed(1) || "5.0"}
              </div>
              <StarDisplay rating={summary?.avg_professional || summary?.avg_professionalism || 5} size="md" />
            </div>
          </CardContent>
        </Card>

        {/* On Time */}
        <Card className="border-gray-100">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#1F2933]/50 mb-1">On Time</p>
              <div className="text-2xl font-bold text-[#1F2933]">
                {summary?.avg_on_time?.toFixed(1) || "5.0"}
              </div>
              <StarDisplay rating={summary?.avg_on_time || 5} size="md" />
            </div>
          </CardContent>
        </Card>

        {/* Quality */}
        <Card className="border-gray-100">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#1F2933]/50 mb-1">Quality</p>
              <div className="text-2xl font-bold text-[#1F2933]">
                {summary?.avg_quality?.toFixed(1) || "5.0"}
              </div>
              <StarDisplay rating={summary?.avg_quality || 5} size="md" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profile Completeness & Tips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProfileCompletenessCard completeness={profile_completeness} />
        <PerformanceTipsCard tips={performance_tips} />
      </div>

      {/* Recent Ratings */}
      <Card className="border-gray-100">
        <CardHeader>
          <CardTitle className="text-lg">Recent Ratings</CardTitle>
          <CardDescription>
            Your last 5 ratings from homeowners (anonymous)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RecentRatingsList ratings={recent_ratings} onFlag={handleFlagClick} />
        </CardContent>
      </Card>

      {/* Flag Dialog */}
      <Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Flag This Rating
            </DialogTitle>
            <DialogDescription>
              If you believe this rating violates our guidelines, you can flag it for admin review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value.slice(0, 500))}
              placeholder="Please explain why you are flagging this rating..."
              className="min-h-[100px]"
            />
            <p className="text-xs text-[#1F2933]/50 text-right">
              {flagReason.length}/500 characters
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setFlagDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                onClick={handleFlagSubmit}
                disabled={flagging || !flagReason.trim()}
              >
                {flagging ? "Flagging..." : "Submit Flag"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
